import React, { useState } from 'react';
import { Sparkles, Heart, Mail, CheckCircle2, ShieldCheck, Globe } from 'lucide-react';

interface FooterProps {
  onOpenSizingModal: () => void;
  onOpenCustomOrderModal: () => void;
}

export const Footer: React.FC<FooterProps> = ({ onOpenSizingModal, onOpenCustomOrderModal }) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setSubscribed(true);
  };

  return (
    <footer className="bg-gradient-to-b from-[#FAF5EE] to-[#F3EBE0] border-t border-[#E7DEC8] text-[#44403C] relative overflow-hidden">
      {/* Top Wave / Pastel Accent */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#D4AF37] via-[#F472B6] via-[#A855F7] to-[#2DD4BF]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          
          {/* Brand Info */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full p-0.5 bg-gradient-to-tr from-[#D4AF37] via-[#F472B6] to-[#67E8F9] shadow-md">
                <img
                  src="/logo.png"
                  alt="Nailed It Logo"
                  className="w-full h-full object-contain rounded-full bg-white"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/Nailed It Logo.png';
                  }}
                />
              </div>
              <div>
                <span className="font-serif text-2xl font-extrabold gold-gradient-text block leading-tight">
                  Nailed It
                </span>
                <span className="text-[10px] font-sans font-bold tracking-[0.2em] text-[#78716C] uppercase">
                  Artistic Nails <span className="text-[#D4AF37] font-script normal-case tracking-normal text-sm font-bold">by Kylee</span>
                </span>
              </div>
            </div>

            <p className="text-xs text-[#57534E] leading-relaxed max-w-sm">
              Hand-painted, reusable gel press-on nails created by 14-year-old competitive gymnast Kylee. Precision artistry, gymnast-grade durability, and salon-quality shine for every occasion.
            </p>

            <div className="flex items-center gap-2 text-xs font-semibold text-[#854D0E] pt-1">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span>All Nail Sets Flat $30 • Free US Shipping over $50</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="font-serif text-sm font-bold text-[#292524] uppercase tracking-wider">
              Explore & Shop
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <a href="#shop" className="hover:text-[#B45309] transition-colors">
                  Shop All $30 Sets
                </a>
              </li>
              <li>
                <a href="#about" className="hover:text-[#B45309] transition-colors">
                  Meet 14yo Artist Kylee
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={onOpenSizingModal}
                  className="hover:text-[#B45309] transition-colors text-left"
                >
                  Interactive Sizing & Care Guide
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={onOpenCustomOrderModal}
                  className="hover:text-[#B45309] transition-colors text-left"
                >
                  Design Custom Set ($30)
                </button>
              </li>
              <li>
                <a href="#reviews" className="hover:text-[#B45309] transition-colors">
                  Customer Reviews (5-Star)
                </a>
              </li>
              <li>
                <a href="#faq" className="hover:text-[#B45309] transition-colors">
                  Frequently Asked Questions
                </a>
              </li>
            </ul>
          </div>

          {/* VIP Nail Club Newsletter */}
          <div className="lg:col-span-4 space-y-3">
            <h3 className="font-serif text-sm font-bold text-[#292524] uppercase tracking-wider">
              Join Kylee’s VIP Sparkle Club
            </h3>
            <p className="text-xs text-[#57534E] leading-relaxed">
              Get 10% off your first order, early access to new limited-edition hand-painted drops, and nail care tutorials!
            </p>

            {subscribed ? (
              <div className="p-3 rounded-xl bg-[#D1FAE5] text-[#065F46] text-xs font-semibold flex items-center gap-2 border border-[#A7F3D0]">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span>You're on the VIP list! Use code <strong>KYLEE10</strong> for 10% off!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <input
                    type="email"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-3.5 py-2.5 rounded-xl bg-white border border-[#E7DEC8] text-xs text-[#292524] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#D4AF37]"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2.5 rounded-xl text-xs font-bold text-white gold-shimmer-btn shrink-0 shadow-sm"
                  >
                    Join
                  </button>
                </div>
              </form>
            )}

            <div className="flex items-center gap-4 pt-2 text-xs text-[#78716C]">
              <span>Follow Kylee:</span>
              <span className="font-bold text-[#292524] hover:text-[#B45309] cursor-pointer">@naileditbykylee</span>
            </div>
          </div>

        </div>

        {/* Bottom Bar: Copyright, Stripe & Cloudflare */}
        <div className="mt-12 pt-6 border-t border-[#E7DEC8] flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[#78716C]">
          <div className="flex items-center gap-1">
            <span>© {new Date().getFullYear()} Nailed It - Artistic Nails by Kylee. Handcrafted with</span>
            <Heart className="w-3.5 h-3.5 text-[#EC4899] fill-[#EC4899] inline" />
          </div>

          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-[#059669]">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Stripe Payments Ready</span>
            </span>
            <span className="flex items-center gap-1 text-[#2563EB]">
              <Globe className="w-3.5 h-3.5" />
              <span>Deployed on Cloudflare</span>
            </span>
          </div>
        </div>

      </div>
    </footer>
  );
};
